import React, {FC, useState, useEffect} from "react"
import {Row, Col} from "react-bootstrap"

import FormattedMessage, {getMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import {H2, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Input from "@irati/ui/src/FormItem/Input"
import Form from "@irati/ui/src/Form"
import adminTracking from "@irati-tracking/admin"
import Validation from "@irati/utils/validation"
import Link from "@irati/ui/src/Link"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import buService from "@irati/billing/services/bu"
import authentication from "@irati/auth"
import {ValidationButton} from "@irati/ui/src/FormItem"
import {ZuoraContactDetails, ZuoraSoldToContactInput} from "@irati/types"
import ErrorText from "@irati/ui/src/ErrorText"

import {StyledCheckbox} from "./styled"
import AbandonModal from "./AbandonModal"

interface Props extends InjectIntl {
  show: boolean
  submit: Promise<any> | null
  onClose: () => void
  onNext: (contact?: ZuoraContactDetails) => void
}

const UpdateContactDetailsModal: FC<Props> = ({show, submit, onClose, onNext, formatMessage}) => {
  const [contact, setContact] = useState<ZuoraContactDetails>()
  const [conditionsCheckbox, setConditionsCheckbox] = useState(false)
  const [load, setLoad] = useState<Promise<void> | null>(submit)
  const [alertError, setAlertError] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string>()
  const [showModal, setShowModal] = useState(show)
  const [showAbandonModal, setShowAbandonModal] = useState(false)

  const schema = Validation.string(
    formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.validation.required"})
  ).max(50, formatMessage({id: "platform.defaultValidation.maxLength"}, {count: 50}))
  const theme = useTheme()

  const handleConditionsTerms = () => {
    setConditionsCheckbox(!conditionsCheckbox)
    setAlertError(false)
  }

  const handleOnClick = () => {
    if (!conditionsCheckbox) {
      setAlertError(true)
    } else {
      updateSoldToContact()
    }
  }

  const getSoldToContact = () => {
    buService.getSoldToContact({businessId: authentication.business().uuid}).then((res) => {
      if (res?.contact) {
        setContact({
          firstName: res.contact.firstName || "",
          lastName: res.contact.lastName || "",
          email: res.contact.email || "",
          telephone: res.contact.telephone || "",
          soldToIsBillTo: res.contact.soldToIsBillTo || false
        })
      }
    })
  }

  const updateSoldToContact = () => {
    const args: ZuoraSoldToContactInput = {
      businessId: authentication.business().uuid,
      ...contact
    }
    const loaded = buService
      .updateSoldToContact({data: args})
      .then(() => {
        onClose()
        onNext(contact)
        adminTracking.hrisUpsellSetup("admin_dysnc_purchase_primary_contact", {action: "purchase"})
      })
      .catch((error: PBError) => {
        const message = getMessage(formatMessage, getErrorMessage(error), getErrorValues(error))
        setErrorMsg(message)
        adminTracking.hrisUpsellSetup("admin_dysnc_purchase_error", {action: "update_contact_error"})
      })
    setLoad(loaded)
  }

  const handleOnReturn = () => {
    setShowAbandonModal(false)
    setShowModal(true)
  }

  const handleOnClose = () => {
    setShowAbandonModal(false)
    onClose()
  }

  const handleOnHide = () => {
    setShowModal(false)
    setShowAbandonModal(true)
  }

  useEffect(() => {
    setShowModal(show)
    if (!show) {
      setAlertError(false)
      setContact(undefined)
      setConditionsCheckbox(false)
      setErrorMsg("")
    } else {
      getSoldToContact()
    }
  }, [show])

  return (
    <>
      <GenericModal customWidth={800} hideActions show={showModal} onHide={handleOnHide}>
        <H2 marginBottom={2}>
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.contactDetails.title" />
        </H2>
        {alertError && (
          <Alert
            type={AlertTypes.error}
            title="admin.components.integration.directorySync.hris.upsell.contactDetails.validation.termsAndConditions"
          />
        )}
        <Body marginBottom={2} maxWidth="100%">
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.contactDetails.subTitle" />
        </Body>
        <Form>
          <Row>
            <Col xs={6} marginHeight={50}>
              <Input
                name="firstName"
                value={contact?.firstName}
                label={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.firstName.label"})}
                placeholder={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.firstName.label"})}
                onChange={(value) => setContact({...contact, firstName: value})}
                validation={schema.matches(
                  /^[a-zA-Z]*$/,
                  "admin.components.integration.directorySync.hris.upsell.contactDetails.name.validation"
                )}
              />
              <Input
                name="lastName"
                value={contact?.lastName}
                label={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.lastName.label"})}
                placeholder={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.lastName.label"})}
                onChange={(value) => setContact({...contact, lastName: value})}
                validation={schema.matches(
                  /^[a-zA-Z]*$/,
                  "admin.components.integration.directorySync.hris.upsell.contactDetails.name.validation"
                )}
              />
            </Col>
            <Col xs={6}>
              <Input
                name="email"
                value={contact?.email}
                label={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.email.label"})}
                placeholder={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.email.label"})}
                onChange={(value) => setContact({...contact, email: value})}
                validation={schema.email(
                  formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.email.validation"})
                )}
              />
              <Input
                name="phone"
                value={contact?.telephone}
                label={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.contactDetails.phoneNumber.label"})}
                placeholder={formatMessage({
                  id: "admin.components.integration.directorySync.hris.upsell.contactDetails.phoneNumber.label"
                })}
                onChange={(value) => setContact({...contact, telephone: value})}
                validation={schema}
              />
            </Col>
          </Row>
          <StyledCheckbox name="termsConditions" checked={conditionsCheckbox} onChange={handleConditionsTerms}>
            <Body color={theme.colors.greys.black} display="inline-block">
              <FormattedMessage
                id="admin.components.integration.directorySync.hris.upsell.step2.conditionsCheckboxLabel"
                values={{
                  link: (
                    <Link
                      cta
                      href={formatMessage({
                        id: "admin.components.integration.directorySync.hris.upsell.step2.conditionsCheckboxLabelLink"
                      })}
                      target="_blank">
                      <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.step2.conditionsCheckboxLinkValue" />
                    </Link>
                  )
                }}
              />
            </Body>
          </StyledCheckbox>
        </Form>
        <Flex justifyContent="flex-end" marginBottom>
          <Flex width="250px">
            <ValidationButton full load={load} primary onClick={handleOnClick}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.contactDetails.button" />
            </ValidationButton>
          </Flex>
        </Flex>
        {errorMsg && (
          <Flex justifyContent="flex-end" marginBottom>
            <ErrorText>{errorMsg}</ErrorText>
          </Flex>
        )}
      </GenericModal>
      <AbandonModal show={showAbandonModal} upsell onClose={handleOnClose} onReturn={handleOnReturn} />
    </>
  )
}

export default injectIntl(UpdateContactDetailsModal)
